import { datadogLogs } from "@datadog/browser-logs";
import { v4 } from "uuid";
import { z } from "zod";

import { canAccessClientStorage } from "@/common/helpers/embed";
import { CustomAnswer } from "@/common/types/customQuestion";

export const MEMBERSHIP_REBUY_PREFILL_PARAM = "rebuyPrefill";
const STORAGE_KEY = "membership-rebuy-prefill";
const MAX_AGE_MS = 30 * 60 * 1000;

const optionalBuyerStringSchema = z
  .string()
  .nullish()
  .transform((value) => value ?? undefined);

const buyerDetailsSchema = z.object({
  address: optionalBuyerStringSchema,
  city: optionalBuyerStringSchema,
  postalCode: optionalBuyerStringSchema,
  region: optionalBuyerStringSchema,
  country: optionalBuyerStringSchema,
  companyName: optionalBuyerStringSchema,
  giveAsOrganism: z
    .boolean()
    .nullish()
    .transform((value) => value ?? undefined),
  customAnswersOfOrder: z
    .array(
      z.object({
        questionId: z.string(),
        answers: z.array(z.string()),
        choiceIds: z.array(z.string()),
      })
    )
    .nullish()
    .transform((value) => value ?? undefined),
});

export type MembershipRebuyBuyerDetails = z.infer<typeof buyerDetailsSchema>;
export type MembershipRebuyBuyerDetailsInput = z.input<typeof buyerDetailsSchema>;

const storedPrefillSchema = z.object({
  token: z.string(),
  pathname: z.string(),
  rateId: z.string(),
  expiresAt: z.number(),
  buyerDetails: buyerDetailsSchema,
});

export const storeMembershipRebuyPrefill = (
  formUrl: string,
  rateId: string,
  buyerDetails: MembershipRebuyBuyerDetailsInput
): string | undefined => {
  if (!canAccessClientStorage()) {
    return undefined;
  }
  const url = new URL(formUrl, window.location.origin);
  if (url.origin !== window.location.origin) {
    datadogLogs.logger.warn("[MembershipRebuy] Buyer prefill requires same-origin navigation");
    return undefined;
  }
  const validatedBuyerDetails = buyerDetailsSchema.safeParse(buyerDetails);
  if (!validatedBuyerDetails.success) {
    datadogLogs.logger.warn("[MembershipRebuy] Invalid buyer prefill", {
      issueCodes: validatedBuyerDetails.error.issues.map((issue) => issue.code),
    });
    return undefined;
  }
  const token = v4();
  try {
    // Only the latest buyer stays in this tab; addresses and answers must never enter URLs or shared caches.
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        token,
        pathname: url.pathname,
        rateId,
        expiresAt: Date.now() + MAX_AGE_MS,
        buyerDetails: validatedBuyerDetails.data,
      })
    );
    return token;
  } catch (error) {
    if (!(error instanceof DOMException)) {
      throw error;
    }
    datadogLogs.logger.warn("[MembershipRebuy] Unable to store buyer prefill", { errorName: error.name });
    return undefined;
  }
};

export const readMembershipRebuyPrefill = (
  token: string | null,
  rateId: string | null
): MembershipRebuyBuyerDetails | undefined => {
  if (!token || !rateId || !canAccessClientStorage()) {
    return undefined;
  }
  try {
    const result = storedPrefillSchema.safeParse(JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? "null"));
    if (!result.success) {
      datadogLogs.logger.warn("[MembershipRebuy] Stored buyer prefill is missing or invalid");
      window.sessionStorage.removeItem(STORAGE_KEY);
      return undefined;
    }
    if (result.data.expiresAt <= Date.now()) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return undefined;
    }
    const stored = result.data;
    if (stored.token !== token || stored.rateId !== rateId || stored.pathname !== window.location.pathname) {
      return undefined;
    }
    return stored.buyerDetails;
  } catch (error) {
    if (!(error instanceof DOMException) && !(error instanceof SyntaxError)) {
      throw error;
    }
    datadogLogs.logger.warn("[MembershipRebuy] Unable to read buyer prefill", { errorName: error.name });
    return undefined;
  }
};

export const prefillMembershipBuyerAnswers = (
  initializedAnswers: CustomAnswer[],
  previousAnswers: MembershipRebuyBuyerDetails["customAnswersOfOrder"]
): CustomAnswer[] =>
  initializedAnswers.map((answer) => {
    const previousAnswer = previousAnswers?.find((previous) => previous.questionId === answer.questionId);
    return previousAnswer
      ? { ...answer, answers: [...previousAnswer.answers], choiceIds: [...previousAnswer.choiceIds] }
      : answer;
  });
